import { useEffect, useMemo, useRef } from 'react';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';
import DisplayAt from '../../components/DisplayAt';
import { useTranslate } from '../../hooks/useTranslate';

export default function CEProductCustomizationDrawerPdp({
  product,
  selectedVariant,
  storeName = 'product-customization-drawer-pdp',
  watchStoreChanges = false,
  language,
  locale,
  country,
  market,
  localization,
  productHandle,
  productType,
  playerHandle,
  competitionHandle,
  showProductImage = true,
  isAlwaysVisible = true,
  displayAt = 'mobile',
  hideAddToCartButton = false,
  isShopByPlayer = false,
}) {
  const normalizedProduct = product ?? null;
  const normalizedVariant = selectedVariant ?? normalizedProduct?.selected_or_first_available_variant ?? null;
  const normalizedProductHandle = productHandle ?? normalizedProduct?.handle ?? null;
  const normalizedProductType = (
    productType ??
    normalizedProduct?.metafields?.custom?.product_type ??
    'jersey'
  ).toLowerCase();

  const { translate } = useTranslate(language ?? locale);

  const formRef = useRef(null);

  // Prevent background scrolling and reset form scroll on drawer open/close
  useEffect(() => {
    const formEl = formRef.current;
    if (!formEl) return;

    const drawer = formEl.closest('x-drawer');
    if (!drawer) return;

    const onDrawerShow = () => {
      document.body.style.overflow = 'hidden';
      if (formRef.current) {
        formRef.current.scrollTop = 0;
      }
    };

    const onDrawerHide = () => {
      document.body.style.overflow = '';
    };

    // Sync initial state
    if (drawer.hasAttribute('open')) {
      onDrawerShow();
    }

    drawer.addEventListener('dialog:after-show', onDrawerShow);
    drawer.addEventListener('dialog:after-hide', onDrawerHide);

    return () => {
      drawer.removeEventListener('dialog:after-show', onDrawerShow);
      drawer.removeEventListener('dialog:after-hide', onDrawerHide);
      document.body.style.overflow = '';
    };
  }, []);

  const resolvedLocalization = useMemo(() => {
    const targetLocale = localization?.locale ?? locale ?? language ?? null;
    const targetCountry = localization?.country ?? country ?? null;
    const targetMarket = localization?.market ?? market ?? null;

    if (targetLocale || targetCountry || targetMarket) {
      return {
        locale: targetLocale ?? undefined,
        country: targetCountry ?? undefined,
        market: targetMarket ?? undefined,
      };
    }

    return undefined;
  }, [localization, locale, language, country, market]);

  const customizationHookConfig = useMemo(
    () => ({
      storeName,
      watchStoreChanges,
      language: language ?? locale ?? resolvedLocalization?.locale,
      country,
      market,
      localization: resolvedLocalization,
      isShopByPlayer,
    }),
    [storeName, watchStoreChanges, language, locale, resolvedLocalization, country, market, isShopByPlayer],
  );

  // Get customization hook for reset functionality
  const { resetCustomization } = useCEProductCustomization({
    storeName,
    watchStoreChanges: false,
    localization: resolvedLocalization,
  });

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    // Reset customization store
    resetCustomization();
  };

  // Close drawer function
  const saveAndClose = (event) => {
    event.target.dispatchEvent(
      new CustomEvent('dialog:force-close', { bubbles: true, cancelable: true, composed: true }),
    );
  };

  if (!normalizedProductHandle) {
    return <div className="product-customization-drawer-pdp__empty">Product data is unavailable.</div>;
  }

  return (
    <DisplayAt value={displayAt}>
      <div className="product-customization-drawer__container">
        <div className="product-customization-drawer__product-preview">
          <div className="product-customization-drawer__product-preview-content">
            <CEProductCustomizationPreview
              productHandle={normalizedProductHandle}
              productType={normalizedProductType}
              competitionHandle={competitionHandle}
              showProductImage={showProductImage}
              isAlwaysVisible={isAlwaysVisible}
              customizationHookConfig={customizationHookConfig}
            />
          </div>
        </div>

        <div className="product-customization-drawer__form" ref={formRef}>
          <CEProductCustomizationForm
            customizationHookConfig={customizationHookConfig}
            showToggle={false}
            isCustomize={true}
            footerLabelClassName="quick-buy-form-container__footer-label-drawer"
          />
        </div>
        {!hideAddToCartButton ? (
          <div className="product-customization-drawer__footer">
            <CEAddToCartButton
              selectedProduct={normalizedProduct}
              selectedVariant={normalizedVariant}
              storeName={storeName}
              onAddToCartSuccess={handleAddToCartSuccess}
              language={language ?? locale}
            >
              <CEProductCustomizationPrice
                storeName={storeName}
                language={language}
                locale={locale}
                showOnlyPrice={true}
                selectedVariant={normalizedVariant}
                currencyCode={normalizedProduct?.currency_code || 'EUR'}
                extraPriceClassName="Body--XS text-end PrimaryFont"
                extraCompareAtPriceClassName="Body--XS line-through text-end PrimaryFont"
              />
            </CEAddToCartButton>
          </div>
        ) : (
          <div className="product-customization-drawer__footer">
            <div className="text-center">
              <button className="continue-button button button-primary w-full om-button" onClick={saveAndClose}>
                <span className="om-button__label PrimaryFont Label--L Medium">
                  <span className="PrimaryFont Label--L Medium ">{translate('customization.form.save_and_close')}</span>
                </span>
                <span class="om-button__icon-wrapper">
                  <svg
                    width="20"
                    class="icon icon-carousel-arrow-right"
                    viewBox="0 0 19 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.9766 9.64258L18.335 9.99805L17.9766 10.3535L9.16406 19.085L8.46094 18.374L16.4082 10.498H0V9.49805H16.4082L8.46094 1.62109L8.8125 1.2666L9.16406 0.911133L17.9766 9.64258Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </DisplayAt>
  );
}
